import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { enqueueSnackbar } from 'notistack';
// @mui
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
//
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
//
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { BackendValidationError } from 'src/utils/backend-validation-error';
import Iconify from 'src/components/iconify';
import { IftaFiling, IftaFuelLine, IftaMileLine, JURISDICTIONS } from 'src/models';
//

// ----------------------------------------------------------------------

type Kind = 'miles' | 'fuel';

type Row = {
  truckNumber: string;
  state: string;
  a: string; // taxable miles | gallons
  b: string; // total miles | amount
  importedFromSystem: string;
  importedFromFile: string;
};

function toRows(filing: IftaFiling, kind: Kind): Row[] {
  if (kind === 'miles') {
    return filing.mileLines.map((line) => ({
      truckNumber: line.truckNumber,
      state: line.state,
      a: String(line.taxableMiles),
      b: String(line.totalMiles),
      importedFromSystem: line.importedFromSystem,
      importedFromFile: line.importedFromFile,
    }));
  }
  return filing.fuelLines.map((line) => ({
    truckNumber: line.truckNumber,
    state: line.state,
    a: String(line.gallons),
    b: String(line.amount),
    importedFromSystem: line.importedFromSystem,
    importedFromFile: line.importedFromFile,
  }));
}

// Flex bases shared by the header and body rows so columns always align.
const COLUMN_FLEX = ['0 0 190px', '0 0 110px', '0 0 130px', '0 0 130px'];

// Prebuilt once: 58 MenuItems per row per render would swamp the reconciler.
const JURISDICTION_MENU_ITEMS = JURISDICTIONS.map((jurisdiction) => (
  <MenuItem key={jurisdiction} value={jurisdiction}>
    {jurisdiction}
  </MenuItem>
));

type EditorRowProps = {
  row: Row;
  index: number;
  striped: boolean;
  translateY: number;
  editing: boolean;
  knownTrucks: string[];
  onStartEdit: (index: number) => void;
  onUpdate: (index: number, field: keyof Row, value: string) => void;
  onRemove: (index: number) => void;
};

const rowSx = {
  display: 'flex',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: 39,
} as const;

const cellSx = (flex: string, extra?: object) => ({
  flex,
  display: 'flex',
  alignItems: 'center',
  ...extra,
});

// Rows render as plain text (cheap to mount, so fast scrolling stays smooth)
// and swap to real inputs only for the row being edited.
const EditorRow = memo(
  ({ row, index, striped, translateY, editing, knownTrucks, onStartEdit, onUpdate, onRemove }: EditorRowProps) => {
    const deleteCell = (
      <TableCell sx={cellSx('0 0 64px', { justifyContent: 'flex-end', pr: { xs: 2, md: 4 } })}>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onRemove(index);
          }}
          aria-label="Remove line"
        >
          <Iconify icon="solar:trash-bin-trash-bold" width={16} sx={{ color: 'error.main' }} />
        </IconButton>
      </TableCell>
    );

    const fromCell = (
      <TableCell sx={cellSx('1 1 auto', { justifyContent: 'flex-end', minWidth: 0 })}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
          {row.importedFromFile}
        </Typography>
      </TableCell>
    );

    if (!editing) {
      return (
        <TableRow
          onClick={() => onStartEdit(index)}
          sx={{
            ...rowSx,
            cursor: 'pointer',
            bgcolor: striped ? '#FAFBFA' : '#FFFFFF',
            '&:hover': { bgcolor: '#F0F3F0' },
          }}
          style={{ transform: `translateY(${translateY}px)` }}
        >
          <TableCell sx={cellSx(COLUMN_FLEX[0], { pl: { xs: 2, md: 4 } })}>
            <Typography variant="body2" noWrap sx={row.truckNumber ? undefined : { color: 'text.disabled' }}>
              {row.truckNumber || 'Truck #'}
            </Typography>
          </TableCell>
          <TableCell sx={cellSx(COLUMN_FLEX[1])}>
            <Typography variant="body2" sx={row.state ? undefined : { color: 'error.main' }}>
              {row.state || 'State'}
            </Typography>
          </TableCell>
          <TableCell sx={cellSx(COLUMN_FLEX[2])}>
            <Typography variant="body2">{row.a}</Typography>
          </TableCell>
          <TableCell sx={cellSx(COLUMN_FLEX[3])}>
            <Typography variant="body2">{row.b}</Typography>
          </TableCell>
          {fromCell}
          {deleteCell}
        </TableRow>
      );
    }

    return (
      <TableRow
        sx={{ ...rowSx, bgcolor: 'rgba(0, 184, 217, 0.06)' }}
        style={{ transform: `translateY(${translateY}px)` }}
      >
        <TableCell sx={cellSx(COLUMN_FLEX[0], { pl: { xs: 2, md: 4 } })}>
          <Autocomplete
            freeSolo
            disableClearable
            size="small"
            sx={{ flex: 1 }}
            options={knownTrucks}
            value={row.truckNumber}
            onInputChange={(_, value) => {
              if (value !== row.truckNumber) {
                onUpdate(index, 'truckNumber', value);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} variant="standard" placeholder="Truck #" />
            )}
          />
        </TableCell>
        <TableCell sx={cellSx(COLUMN_FLEX[1])}>
          <TextField
            select
            size="small"
            variant="standard"
            value={row.state}
            error={!row.state}
            onChange={(event) => onUpdate(index, 'state', event.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 72 }}
          >
            <MenuItem value="" disabled>
              State
            </MenuItem>
            {JURISDICTION_MENU_ITEMS}
          </TextField>
        </TableCell>
        <TableCell sx={cellSx(COLUMN_FLEX[2])}>
          <TextField
            size="small"
            variant="standard"
            type="number"
            value={row.a}
            onChange={(event) => onUpdate(index, 'a', event.target.value)}
          />
        </TableCell>
        <TableCell sx={cellSx(COLUMN_FLEX[3])}>
          <TextField
            size="small"
            variant="standard"
            type="number"
            value={row.b}
            onChange={(event) => onUpdate(index, 'b', event.target.value)}
          />
        </TableCell>
        {fromCell}
        {deleteCell}
      </TableRow>
    );
  }
);
EditorRow.displayName = 'EditorRow';

export type LinesEditorHandle = {
  save: () => Promise<boolean>;
};

export type EditsState = {
  count: number;
  invalid: boolean;
};

type Props = {
  filing: IftaFiling;
  kind: Kind;
  onSaved: (filing: IftaFiling) => void;
  onEditsChange: (edits: EditsState) => void;
  // Shared between the Miles and Fuel tabs so the pick follows you across.
  truckFilter: string | null;
  onTruckFilterChange: (value: string | null) => void;
};

const LinesEditor = forwardRef<LinesEditorHandle, Props>(
  ({ filing, kind, onSaved, onEditsChange, truckFilter, onTruckFilterChange }, ref) => {
    const [rows, setRows] = useState<Row[]>(() => toRows(filing, kind));
    const [saving, setSaving] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'save' | 'discard' | null>(null);
    const [filterMenuAnchor, setFilterMenuAnchor] = useState<HTMLElement | null>(null);
    // Only one row carries the heavy input widgets at a time (click-to-edit).
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [filterSearch, setFilterSearch] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);

    const closeFilterMenu = () => {
      setFilterMenuAnchor(null);
      setFilterSearch('');
    };

    // Dirtiness is derived by diffing against the saved filing, never tracked by
    // event side effects (Autocomplete fires input events on mount).
    const originalRows = useMemo(() => toRows(filing, kind), [filing, kind]);
    const editCount = useMemo(() => {
      let count = Math.abs(rows.length - originalRows.length);
      const overlap = Math.min(rows.length, originalRows.length);
      for (let i = 0; i < overlap; i += 1) {
        if (JSON.stringify(rows[i]) !== JSON.stringify(originalRows[i])) {
          count += 1;
        }
      }
      return count;
    }, [rows, originalRows]);
    const dirty = editCount > 0;

    // Truck suggestions come from the saved filing (both sides). Deliberately
    // not from the live rows: a stable reference keeps memoized rows from
    // re-rendering on every keystroke.
    const knownTrucks = useMemo(
      () =>
        Array.from(
          new Set(
            [...filing.mileLines, ...filing.fuelLines]
              .map((line) => line.truckNumber.trim())
              .filter(Boolean)
          )
        ).sort(),
      [filing]
    );

    const filteredTruckOptions = knownTrucks.filter((truck) =>
      truck.toLowerCase().includes(filterSearch.trim().toLowerCase())
    );

    const hasInvalidState = rows.some((row) => !row.state);

    const headers =
      kind === 'miles'
        ? ['Truck', 'State', 'Taxable Miles', 'Total Miles']
        : ['Truck', 'State', 'Gallons', 'Amount ($)'];

    // Rows shown in the table; each keeps its index into `rows` so edits with an
    // active truck filter land on the right line.
    const visibleRows = useMemo(
      () =>
        rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => !truckFilter || row.truckNumber.trim() === truckFilter),
      [rows, truckFilter]
    );

    const virtualizer = useVirtualizer({
      count: visibleRows.length,
      getScrollElement: () => scrollRef.current,
      // Rows are uniform 39px (single-line standard inputs); fixed sizing keeps
      // scrolling perfectly smooth with no measurement corrections.
      estimateSize: () => 39,
      overscan: 12,
    });
    const virtualItems = virtualizer.getVirtualItems();

    const updateRow = useCallback((index: number, field: keyof Row, value: string) => {
      setRows((current) =>
        current.map((row, i) => (i === index ? { ...row, [field]: value } : row))
      );
    }, []);

    const discardEdits = () => {
      setRows(toRows(filing, kind));
      setEditingIndex(null);
      setConfirmAction(null);
    };

    const removeRow = useCallback((index: number) => {
      setRows((current) => current.filter((_, i) => i !== index));
    }, []);

    const addRow = () => {
      setEditingIndex(rows.length);
      setTimeout(() => virtualizer.scrollToIndex(visibleRows.length, { align: 'end' }), 0);
      setRows((current) => [
        ...current,
        // With a filter active the new line starts on that truck, so it stays visible.
        {
          truckNumber: truckFilter || '',
          state: '',
          a: '0',
          b: '0',
          importedFromSystem: 'manual',
          importedFromFile: 'manual entry',
        },
      ]);
    };

    const save = async (): Promise<boolean> => {
      setSaving(true);
      try {
        const endpoint =
          kind === 'miles' ? endpoints.filings.updateMileLines : endpoints.filings.updateFuelLines;
        const payload =
          kind === 'miles'
            ? {
                mileLines: rows.map((row): IftaMileLine => ({
                  truckNumber: row.truckNumber.trim(),
                  state: row.state.trim(),
                  taxableMiles: Number(row.a) || 0,
                  totalMiles: Number(row.b) || 0,
                  importedFromSystem: row.importedFromSystem,
                  importedFromFile: row.importedFromFile,
                })),
              }
            : {
                fuelLines: rows.map((row): IftaFuelLine => ({
                  truckNumber: row.truckNumber.trim(),
                  state: row.state.trim(),
                  gallons: Number(row.a) || 0,
                  amount: Number(row.b) || 0,
                  importedFromSystem: row.importedFromSystem,
                  importedFromFile: row.importedFromFile,
                })),
              };
        const response = await axios.put(formatEndpoint(endpoint, { id: filing.id }), payload);
        onSaved(response.data.filing);
        setRows(toRows(response.data.filing, kind));
        enqueueSnackbar(
          kind === 'miles'
            ? 'Successfully saved your Mile edits'
            : 'Successfully saved your Fuel edits',
          { variant: 'success' }
        );
        return true;
      } catch (error) {
        const messages: BackendValidationError[] = error?.response?.data;
        if (Array.isArray(messages)) {
          messages.forEach((message) => enqueueSnackbar(message.message, { variant: 'error' }));
        } else {
          enqueueSnackbar('Could not save your changes', { variant: 'error' });
        }
        return false;
      } finally {
        setSaving(false);
      }
    };

    useEffect(() => {
      onEditsChange({ count: editCount, invalid: hasInvalidState });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editCount, hasInvalidState]);

    useImperativeHandle(ref, () => ({ save }));

    return (
      <Stack spacing={2}>
        {/* full-bleed: cancel the wizard card padding so the table reaches the edges */}
        <TableContainer
          ref={scrollRef}
          sx={{ maxHeight: 420, width: 'auto', mx: { xs: -2, md: -4 } }}
        >
          <Table size="small" data-testid={`${kind}-editor`} sx={{ display: 'block', minWidth: 680 }}>
            <TableHead
              sx={{ display: 'block', position: 'sticky', top: 0, zIndex: 2 }}
            >
              <TableRow sx={{ display: 'flex' }}>
                {headers.map((header, index) => (
                  <TableCell
                    key={header}
                    sx={{
                      flex: COLUMN_FLEX[index],
                      display: 'flex',
                      alignItems: 'center',
                      ...(index === 0 && { pl: { xs: 2, md: 4 } }),
                    }}
                  >
                    {index === 0 ? (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box component="span">{header}</Box>
                        <IconButton
                          size="small"
                          onClick={(event) => setFilterMenuAnchor(event.currentTarget)}
                          sx={{ color: truckFilter ? 'info.main' : 'text.disabled' }}
                          aria-label="Filter by truck"
                          data-testid={`${kind}-truck-filter`}
                        >
                          <Iconify icon="eva:search-fill" width={16} />
                        </IconButton>
                        {truckFilter && (
                          <Chip
                            size="small"
                            color="info"
                            label={truckFilter}
                            onClick={(event) => setFilterMenuAnchor(event.currentTarget)}
                            onDelete={() => onTruckFilterChange(null)}
                            sx={{
                              color: '#FFFFFF',
                              fontWeight: 600,
                              height: 22,
                              '& .MuiChip-deleteIcon': { color: 'rgba(255, 255, 255, 0.72)' },
                              '& .MuiChip-deleteIcon:hover': { color: '#FFFFFF' },
                            }}
                            data-testid={`${kind}-truck-filter-chip`}
                          />
                        )}
                      </Stack>
                    ) : (
                      header
                    )}
                  </TableCell>
                ))}
                <TableCell
                  sx={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                >
                  From
                </TableCell>
                <TableCell sx={{ flex: '0 0 64px', pr: { xs: 2, md: 4 } }} />
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                display: 'block',
                position: 'relative',
                height: Math.max(virtualizer.getTotalSize(), visibleRows.length === 0 ? 72 : 0),
              }}
            >
              {visibleRows.length === 0 && truckFilter && (
                <TableRow sx={{ display: 'flex', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                  <TableCell
                    sx={{ flex: '1 1 auto', py: 4, textAlign: 'center', color: 'text.secondary', border: 0 }}
                  >
                    No {kind === 'miles' ? 'mile' : 'fuel'} lines for truck {truckFilter}
                  </TableCell>
                </TableRow>
              )}
              {virtualItems.map((virtualRow) => {
                const { row, index } = visibleRows[virtualRow.index];
                return (
                  <EditorRow
                    key={index}
                    row={row}
                    index={index}
                    striped={virtualRow.index % 2 === 1}
                    translateY={virtualRow.start}
                    editing={editingIndex === index}
                    knownTrucks={knownTrucks}
                    onStartEdit={setEditingIndex}
                    onUpdate={updateRow}
                    onRemove={removeRow}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={closeFilterMenu}
          slotProps={{ paper: { sx: { width: 220, maxHeight: 340 } } }}
        >
          {knownTrucks.length > 8 && (
            <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder="Search trucks"
                value={filterSearch}
                onChange={(event) => setFilterSearch(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
              />
            </Box>
          )}
          {filteredTruckOptions.map((truck) => (
            <MenuItem
              key={truck}
              selected={truck === truckFilter}
              onClick={() => {
                onTruckFilterChange(truck);
                closeFilterMenu();
              }}
            >
              {truck}
            </MenuItem>
          ))}
          {filteredTruckOptions.length === 0 && <MenuItem disabled>No trucks found</MenuItem>}
        </Menu>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexWrap: 'wrap', rowGap: 1.5, columnGap: 1.5, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Button
              size="small"
              color="inherit"
              startIcon={<Iconify icon="mingcute:add-line" width={16} />}
              onClick={addRow}
              data-testid={`${kind}-add-line`}
            >
              Add line
            </Button>
            {truckFilter && (
              <Typography
                variant="caption"
                sx={{ color: 'info.main' }}
                data-testid={`${kind}-filter-count`}
              >
                Showing {visibleRows.length} of {rows.length} lines
              </Typography>
            )}
          </Stack>
          {dirty && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
              {hasInvalidState && (
                <Typography variant="caption" sx={{ color: 'error.main' }}>
                  Pick a state for every line before saving
                </Typography>
              )}
              <Button
                size="small"
                color="inherit"
                onClick={() => setConfirmAction('discard')}
                data-testid={`${kind}-discard-lines`}
              >
                Discard changes
              </Button>
              <LoadingButton
                size="small"
                variant="contained"
                color="info"
                loading={saving}
                disabled={hasInvalidState}
                onClick={() => setConfirmAction('save')}
                startIcon={<Iconify icon="solar:diskette-bold" width={16} />}
                sx={{ color: '#FFFFFF' }}
                data-testid={`${kind}-save-lines`}
              >
                Save changes
              </LoadingButton>
            </Stack>
          )}
        </Stack>

        <Dialog
          open={confirmAction !== null}
          onClose={() => setConfirmAction(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {confirmAction === 'save' ? 'Save these changes?' : 'Discard your edits?'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {confirmAction === 'save'
                ? 'Your edited lines replace the current data and we recalculate the filing.'
                : 'Your unsaved edits on this tab are lost and the lines go back to their last saved state.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={() => setConfirmAction(null)}>
              Keep editing
            </Button>
            {confirmAction === 'save' ? (
              <LoadingButton
                variant="contained"
                color="info"
                loading={saving}
                onClick={async () => {
                  await save();
                  setConfirmAction(null);
                }}
                sx={{ color: '#FFFFFF' }}
                data-testid={`${kind}-confirm-save`}
              >
                Save changes
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                color="error"
                onClick={discardEdits}
                data-testid={`${kind}-confirm-discard`}
              >
                Discard edits
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }
);

LinesEditor.displayName = 'LinesEditor';

export default LinesEditor;
